import React, { useEffect, useState } from 'react';
import { HardDrive, AlertCircle, CheckCircle } from 'lucide-react';
import StorageQuotaService from '../services/storageQuotaService';
import type { StorageStatus } from '../services/storageQuotaService';

const StorageUsageDisplay: React.FC = () => {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const quotaService = StorageQuotaService.getInstance();

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await quotaService.getStorageStatus(true); // Force refresh
      setStatus(data);
    } catch (err) {
      console.error('[StorageUsage] Error loading status:', err);
      setError('Failed to load storage information');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle size={20} />
          <p className="text-sm">{error || 'Unable to load storage information'}</p>
        </div>
      </div>
    );
  }

  const warningLevel = quotaService.getWarningLevel(status.percent_used);
  
  // Determine colors based on warning level
  const getColors = () => {
    switch (warningLevel) {
      case 'critical':
        return {
          gradient: 'from-red-500 to-rose-600',
          bg: 'from-red-50 to-rose-50',
          border: 'border-red-200',
          text: 'text-red-900',
          progressBar: 'bg-red-600',
          icon: 'text-red-600'
        };
      case 'warning':
        return {
          gradient: 'from-orange-500 to-amber-600',
          bg: 'from-orange-50 to-amber-50',
          border: 'border-orange-200',
          text: 'text-orange-900',
          progressBar: 'bg-orange-500',
          icon: 'text-orange-600'
        };
      default:
        return {
          gradient: 'from-blue-500 to-indigo-600',
          bg: 'from-blue-50 to-indigo-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          progressBar: 'bg-blue-600',
          icon: 'text-blue-600'
        };
    }
  };

  const colors = getColors();

  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
      <div className="flex items-center mb-6">
        <div className={`p-3 bg-gradient-to-r ${colors.gradient} rounded-xl shadow-lg`}>
          <HardDrive className="text-white" size={24} />
        </div>
        <h2 className={`text-2xl font-bold ml-4 bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
          Cloud Storage
        </h2>
      </div>

      <div className="space-y-4">
        {/* Storage Stats */}
        <div className={`bg-gradient-to-r ${colors.bg} border-2 ${colors.border} rounded-xl p-5`}>
          {/* Usage Summary */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Storage Used</p>
              <p className={`text-2xl font-bold ${colors.text}`}>
                {status.used_gb.toFixed(2)} GB
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-600 mb-1">Total Available</p>
              <p className={`text-2xl font-bold ${colors.text}`}>
                {status.quota_gb} GB
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
              <span className="font-medium">Usage: {status.percent_used.toFixed(1)}%</span>
              <span className="font-medium">{status.available_gb.toFixed(2)} GB remaining</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div 
                className={`h-3 rounded-full ${colors.progressBar} transition-all duration-500 ease-out shadow-sm`}
                style={{ width: `${Math.min(status.percent_used, 100)}%` }}
              />
            </div>
          </div>

          {/* Warning Messages */}
          {warningLevel === 'critical' && (
            <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-semibold text-red-900">Storage Almost Full!</p>
                <p className="text-xs text-red-800 mt-1">
                  New files will be saved to your device instead of cloud. Consider upgrading your plan or deleting unused files.
                </p>
              </div>
            </div>
          )}

          {warningLevel === 'warning' && (
            <div className="mt-4 p-3 bg-orange-100 border border-orange-300 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-semibold text-orange-900">Storage Running Low</p>
                <p className="text-xs text-orange-800 mt-1">
                  You're using {status.percent_used.toFixed(0)}% of your storage. Consider cleaning up old files or upgrading your plan.
                </p>
              </div>
            </div>
          )}

          {warningLevel === 'normal' && status.percent_used < 50 && (
            <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-semibold text-green-900">Storage Healthy</p>
                <p className="text-xs text-green-800 mt-1">
                  You have plenty of space available ({status.available_gb.toFixed(1)} GB remaining).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">💡 Storage Information</p>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• When storage is full, new files save to your device automatically</li>
            <li>• Access device files: Files app → NoorCAD (iOS) or Documents folder (Android)</li>
            <li>• Upgrade to Pro for 250 GB storage ({status.quota_gb === 50 ? '5x more space' : 'maximum capacity'})</li>
          </ul>
        </div>

        {/* Refresh Button */}
        <button
          onClick={loadStatus}
          disabled={isLoading}
          className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50"
        >
          {isLoading ? 'Refreshing...' : 'Refresh Storage Info'}
        </button>
      </div>
    </div>
  );
};

export default StorageUsageDisplay;
